import { useEffect, useState } from "react";
import { Download, Plus, Trash2 } from "lucide-react";
import { api, money } from "@/lib/api";

export const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const PTKP_OPTIONS = ["TK/0", "TK/1", "TK/2", "TK/3", "K/0", "K/1", "K/2", "K/3"];

export function MasaPicker({ year, month, setYear, setMonth, prefix, children }) {
  return (
    <div className="workflow-toolbar surface prod-toolbar">
      <label className="gl-account-picker">Masa pajak
        <select data-testid={`${prefix}-month-select`} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      </label>
      <label className="gl-account-picker">Tahun
        <select data-testid={`${prefix}-year-select`} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[year - 2, year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </label>
      {children}
    </div>
  );
}

function downloadCsv(name, header, lines) {
  const blob = new Blob(["\uFEFF" + [header, ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function Pph21Report() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ employee_name: "", npwp: "", ptkp_status: "TK/0", gross: "" });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api(`/reports/pph21?year=${year}&month=${month}`).then(setData).catch((e) => setError(e.message));
  useEffect(() => { setData(null); load(); }, [year, month]); // eslint-disable-line
  useEffect(() => {
    const gross = Number(form.gross);
    if (!gross || gross <= 0) { setPreview(null); return; }
    let active = true;
    const t = setTimeout(() => api(`/tax/pph21-preview?gross=${gross}&ptkp=${encodeURIComponent(form.ptkp_status)}`).then((p) => active && setPreview(p)).catch(() => {}), 250);
    return () => { active = false; clearTimeout(t); };
  }, [form.gross, form.ptkp_status]);
  const submit = async () => {
    setSaving(true); setError("");
    try {
      await api("/pph21-records", { method: "POST", body: JSON.stringify({ ...form, gross: Number(form.gross), year, month }) });
      setForm({ employee_name: "", npwp: "", ptkp_status: "TK/0", gross: "" });
      setPreview(null);
      await load();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  const remove = async (id) => {
    try { await api(`/pph21-records/${id}`, { method: "DELETE" }); await load(); } catch (e) { setError(e.message); }
  };
  const exportCsv = () => downloadCsv(`pph21-${data.masa}.csv`, "Nama;NPWP;Status PTKP;Kategori TER;Bruto;Tarif %;PPh 21",
    data.records.map((r) => [r.employee_name, r.npwp || "", r.ptkp_status, r.category, r.gross, r.rate, r.pph21].join(";")));
  if (error && !data) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Memuat PPh 21...</div>;
  return (
    <div data-testid="pph21-report">
      <MasaPicker year={year} month={month} setYear={setYear} setMonth={setMonth} prefix="pph21">
        <button data-testid="pph21-export-csv" className="secondary-btn small" onClick={exportCsv}><Download size={14} /> Ekspor CSV (bukti potong)</button>
      </MasaPicker>
      {error && <div className="success-message error" data-testid="pph21-error">{error}</div>}
      <div className="exp-cards" data-testid="pph21-summary-cards">
        <div className="exp-card"><div className="exp-card-head"><span>Jumlah Karyawan</span></div><div className="exp-card-body"><small>Masa {MONTHS[month - 1]} {year}</small><strong data-testid="pph21-count">{data.employee_count}</strong></div></div>
        <div className="exp-card"><div className="exp-card-head"><span>Total Penghasilan Bruto</span></div><div className="exp-card-body"><small>Masa {MONTHS[month - 1]} {year}</small><strong data-testid="pph21-total-gross">{money(data.total_gross)}</strong></div></div>
        <div className="exp-card"><div className="exp-card-head"><span>Total PPh 21 Dipotong</span></div><div className="exp-card-body"><small>Setor sebelum {data.due_date?.split("-").reverse().join("-")}</small><strong data-testid="pph21-total">{money(data.total_pph21)}</strong></div></div>
      </div>
      <div className="surface tax-form" data-testid="pph21-form">
        <h3><Plus size={15} /> Hitung & catat PPh 21 karyawan — metode TER (PP 58/2023)</h3>
        <div className="tax-form-grid">
          <label>Nama karyawan<input data-testid="pph21-name-input" value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} placeholder="Nama lengkap" /></label>
          <label>NPWP / NIK<input data-testid="pph21-npwp-input" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} placeholder="Opsional" /></label>
          <label>Status PTKP
            <select data-testid="pph21-ptkp-select" value={form.ptkp_status} onChange={(e) => setForm({ ...form, ptkp_status: e.target.value })}>
              {PTKP_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label>Penghasilan bruto sebulan (Rp)<input data-testid="pph21-gross-input" type="number" min="0" value={form.gross} onChange={(e) => setForm({ ...form, gross: e.target.value })} placeholder="mis. 10000000" /></label>
        </div>
        {preview && (
          <div className="tax-preview" data-testid="pph21-preview">
            <span>Kategori TER <b>{preview.category}</b></span>
            <span>Tarif <b>{preview.rate}%</b></span>
            <span>PPh 21 sebulan <b data-testid="pph21-preview-amount">{money(preview.amount)}</b></span>
            <span className="muted">Estimasi setahun (Pasal 17): PKP {money(preview.annual.pkp)} → {money(preview.annual.pph21_setahun)}</span>
          </div>
        )}
        <div className="tax-form-actions">
          <button data-testid="pph21-save-button" className="primary-btn" disabled={!form.employee_name.trim() || !Number(form.gross) || saving} onClick={submit}>{saving ? "Menyimpan..." : `Simpan ke masa ${MONTHS[month - 1]} ${year}`}</button>
        </div>
      </div>
      <div className="surface workflow-table-wrap auto-h">
        <table className="workflow-table" data-testid="pph21-table">
          <thead><tr><th>NAMA</th><th>NPWP</th><th>PTKP</th><th>KATEGORI TER</th><th className="right">BRUTO</th><th className="right">TARIF</th><th className="right">PPH 21</th><th /></tr></thead>
          <tbody>
            {data.records.map((r, i) => (
              <tr key={r.id} data-testid={`pph21-row-${i}`}>
                <td className="primary-cell">{r.employee_name}</td>
                <td>{r.npwp || "—"}</td>
                <td>{r.ptkp_status}</td>
                <td>{r.category}</td>
                <td className="right mono">{money(r.gross)}</td>
                <td className="right mono">{r.rate}%</td>
                <td className="right mono">{money(r.pph21)}</td>
                <td><button data-testid={`pph21-delete-${i}`} className="icon-btn" title="Hapus" onClick={() => remove(r.id)}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="report-total-row">
              <td colSpan={4}><strong>TOTAL</strong></td>
              <td className="right mono"><strong>{money(data.total_gross)}</strong></td><td />
              <td className="right mono" data-testid="pph21-table-total"><strong>{money(data.total_pph21)}</strong></td><td />
            </tr>
          </tfoot>
        </table>
        {data.records.length === 0 && <div className="empty-state" data-testid="pph21-empty"><strong>Belum ada data PPh 21</strong><span>Tambahkan perhitungan karyawan pada masa ini lewat form di atas.</span></div>}
      </div>
      {month === 12 && <div className="surface tax-note" data-testid="pph21-december-note">Masa Desember: lakukan rekonsiliasi tahunan memakai tarif progresif Pasal 17 — potongan Desember = PPh setahun dikurangi total yang sudah dipotong Januari–November.</div>}
      <div className="surface workflow-table-wrap ppn-monthly auto-h">
        <table className="workflow-table" data-testid="pph21-monthly-table">
          <thead><tr><th>MASA {year}</th><th className="right">KARYAWAN</th><th className="right">TOTAL BRUTO</th><th className="right">TOTAL PPH 21</th></tr></thead>
          <tbody>
            {data.monthly.map((m, i) => (
              <tr key={m.masa} className={m.masa === data.masa ? "recon-row-matched" : ""} data-testid={`pph21-month-${i}`}>
                <td className="primary-cell">{MONTHS[i]}</td>
                <td className="right mono">{m.count}</td>
                <td className="right mono">{money(m.gross)}</td>
                <td className="right mono">{money(m.pph21)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Pph23Report() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ vendor_name: "", npwp: "", has_npwp: true, object_type: "JASA", dpp: "", description: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api(`/reports/pph23?year=${year}&month=${month}`).then(setData).catch((e) => setError(e.message));
  useEffect(() => { api("/tax/objects").then(setMeta).catch(() => {}); }, []);
  useEffect(() => { setData(null); load(); }, [year, month]); // eslint-disable-line
  const baseRate = meta?.pph23_rates?.[form.object_type] ?? 0;
  const effRate = form.has_npwp ? baseRate : baseRate * (meta?.non_npwp_multiplier ?? 2);
  const previewAmount = Number(form.dpp) > 0 ? (Number(form.dpp) * effRate) / 100 : 0;
  const submit = async () => {
    setSaving(true); setError("");
    try {
      await api("/pph23-records", { method: "POST", body: JSON.stringify({ ...form, dpp: Number(form.dpp), year, month }) });
      setForm({ vendor_name: "", npwp: "", has_npwp: true, object_type: "JASA", dpp: "", description: "" });
      await load();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  const remove = async (id) => {
    try { await api(`/pph23-records/${id}`, { method: "DELETE" }); await load(); } catch (e) { setError(e.message); }
  };
  const exportCsv = () => downloadCsv(`pph23-${data.masa}.csv`, "Penerima;NPWP;Objek;DPP;Tarif %;PPh 23;Keterangan",
    data.records.map((r) => [r.vendor_name, r.npwp || "", r.object_label, r.dpp, r.rate, r.pph23, (r.description || "").replace(/;/g, ",")].join(";")));
  if (error && !data) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Memuat PPh 23...</div>;
  return (
    <div data-testid="pph23-report">
      <MasaPicker year={year} month={month} setYear={setYear} setMonth={setMonth} prefix="pph23">
        <button data-testid="pph23-export-csv" className="secondary-btn small" onClick={exportCsv}><Download size={14} /> Ekspor CSV (bukti potong)</button>
      </MasaPicker>
      {error && <div className="success-message error" data-testid="pph23-error">{error}</div>}
      <div className="exp-cards" data-testid="pph23-summary-cards">
        <div className="exp-card"><div className="exp-card-head"><span>Jumlah Pemotongan</span></div><div className="exp-card-body"><small>Masa {MONTHS[month - 1]} {year}</small><strong data-testid="pph23-count">{data.records.length}</strong></div></div>
        <div className="exp-card"><div className="exp-card-head"><span>Total DPP</span></div><div className="exp-card-body"><small>Dasar pengenaan pajak</small><strong data-testid="pph23-total-dpp">{money(data.total_dpp)}</strong></div></div>
        <div className="exp-card"><div className="exp-card-head"><span>Total PPh 23 Dipotong</span></div><div className="exp-card-body"><small>Setor sebelum {data.due_date?.split("-").reverse().join("-")}</small><strong data-testid="pph23-total">{money(data.total_pph23)}</strong></div></div>
      </div>
      <div className="surface tax-form" data-testid="pph23-form">
        <h3><Plus size={15} /> Catat pemotongan PPh 23</h3>
        <div className="tax-form-grid">
          <label>Nama penerima penghasilan<input data-testid="pph23-vendor-input" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} placeholder="Nama vendor / pihak" /></label>
          <label>NPWP<input data-testid="pph23-npwp-input" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} placeholder="Opsional" /></label>
          <label>Jenis objek
            <select data-testid="pph23-object-select" value={form.object_type} onChange={(e) => setForm({ ...form, object_type: e.target.value })}>
              {Object.entries(data.objects).map(([k, v]) => <option key={k} value={k}>{`${v} — ${meta?.pph23_rates?.[k] ?? ""}%`}</option>)}
            </select>
          </label>
          <label>DPP / jumlah bruto (Rp)<input data-testid="pph23-dpp-input" type="number" min="0" value={form.dpp} onChange={(e) => setForm({ ...form, dpp: e.target.value })} placeholder="mis. 5000000" /></label>
          <label>Keterangan<input data-testid="pph23-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Opsional" /></label>
          <label className="tax-check"><input data-testid="pph23-npwp-check" type="checkbox" checked={form.has_npwp} onChange={(e) => setForm({ ...form, has_npwp: e.target.checked })} /> Penerima memiliki NPWP (tanpa NPWP tarif ×{meta?.non_npwp_multiplier ?? 2})</label>
        </div>
        {previewAmount > 0 && (
          <div className="tax-preview" data-testid="pph23-preview">
            <span>Tarif efektif <b>{effRate}%</b></span>
            <span>PPh 23 dipotong <b data-testid="pph23-preview-amount">{money(previewAmount)}</b></span>
          </div>
        )}
        <div className="tax-form-actions">
          <button data-testid="pph23-save-button" className="primary-btn" disabled={!form.vendor_name.trim() || !Number(form.dpp) || saving} onClick={submit}>{saving ? "Menyimpan..." : `Simpan ke masa ${MONTHS[month - 1]} ${year}`}</button>
        </div>
      </div>
      <div className="surface workflow-table-wrap auto-h">
        <table className="workflow-table" data-testid="pph23-table">
          <thead><tr><th>PENERIMA</th><th>NPWP</th><th>OBJEK</th><th className="right">DPP</th><th className="right">TARIF</th><th className="right">PPH 23</th><th /></tr></thead>
          <tbody>
            {data.records.map((r, i) => (
              <tr key={r.id} data-testid={`pph23-row-${i}`}>
                <td className="primary-cell">{r.vendor_name}</td>
                <td>{r.has_npwp ? r.npwp || "Ada" : "Tanpa NPWP"}</td>
                <td>{r.object_label}</td>
                <td className="right mono">{money(r.dpp)}</td>
                <td className="right mono">{r.rate}%</td>
                <td className="right mono">{money(r.pph23)}</td>
                <td><button data-testid={`pph23-delete-${i}`} className="icon-btn" title="Hapus" onClick={() => remove(r.id)}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="report-total-row">
              <td colSpan={3}><strong>TOTAL</strong></td>
              <td className="right mono"><strong>{money(data.total_dpp)}</strong></td><td />
              <td className="right mono" data-testid="pph23-table-total"><strong>{money(data.total_pph23)}</strong></td><td />
            </tr>
          </tfoot>
        </table>
        {data.records.length === 0 && <div className="empty-state" data-testid="pph23-empty"><strong>Belum ada pemotongan PPh 23</strong><span>Catat pemotongan pada masa ini lewat form di atas.</span></div>}
      </div>
      <div className="surface workflow-table-wrap ppn-monthly auto-h">
        <table className="workflow-table" data-testid="pph23-monthly-table">
          <thead><tr><th>MASA {year}</th><th className="right">JUMLAH</th><th className="right">TOTAL DPP</th><th className="right">TOTAL PPH 23</th></tr></thead>
          <tbody>
            {data.monthly.map((m, i) => (
              <tr key={m.masa} className={m.masa === data.masa ? "recon-row-matched" : ""} data-testid={`pph23-month-${i}`}>
                <td className="primary-cell">{MONTHS[i]}</td>
                <td className="right mono">{m.count}</td>
                <td className="right mono">{money(m.dpp)}</td>
                <td className="right mono">{money(m.pph23)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PphBadan() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [settings, setSettings] = useState(null);
  const [pl, setPl] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api("/tax-settings").then(setSettings).catch((e) => setError(e.message)); }, []);
  useEffect(() => {
    let active = true;
    setPl(null);
    api(`/reports/profit-loss?start=${year}-01-01&end=${year}-12-31`).then((d) => active && setPl(d)).catch((e) => active && setError(e.message));
    return () => { active = false; };
  }, [year]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!settings || !pl) return <div className="report-loading" data-testid="report-loading">Menghitung estimasi PPh tahunan...</div>;
  const isUmkm = settings.wp_type === "UMKM_FINAL";
  const isPribadi = settings.wp_type === "PRIBADI";
  const base = isUmkm ? pl.total_revenue : Math.max(0, pl.net_income);
  const rate = isUmkm ? settings.pph_final_umkm_rate : settings.pph_badan_rate;
  const estimate = isPribadi ? null : (base * rate) / 100;
  const typeLabel = isUmkm ? `PPh Final UMKM ${settings.pph_final_umkm_rate}% dari omzet (PP 55/2022)` : isPribadi ? "Orang Pribadi — tarif progresif Pasal 17" : `PPh Badan ${settings.pph_badan_rate}% dari laba fiskal`;
  return (
    <div data-testid="pph-badan-report">
      <div className="workflow-toolbar surface prod-toolbar">
        <label className="gl-account-picker">Tahun pajak
          <select data-testid="badan-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year - 2, year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <span className="record-count" data-testid="badan-type-label">{typeLabel} — ubah di Pengaturan Pajak</span>
      </div>
      <div className="exp-cards" data-testid="badan-summary-cards">
        <div className="exp-card"><div className="exp-card-head"><span>Omzet (Pendapatan) {year}</span></div><div className="exp-card-body"><small>Dari Laba Rugi</small><strong data-testid="badan-revenue">{money(pl.total_revenue)}</strong></div></div>
        <div className="exp-card"><div className="exp-card-head"><span>Laba Bersih {year}</span></div><div className="exp-card-body"><small>Sebelum koreksi fiskal</small><strong data-testid="badan-net-income">{money(pl.net_income)}</strong></div></div>
        <div className="exp-card"><div className="exp-card-head"><span>Estimasi PPh Tahunan</span></div><div className="exp-card-body"><small>{isPribadi ? "Gunakan tarif progresif Pasal 17" : `${rate}% × ${isUmkm ? "omzet" : "laba"}`}</small><strong data-testid="badan-estimate">{estimate === null ? "Lihat SPT OP" : money(estimate)}</strong></div></div>
      </div>
      <div className="surface tax-note" data-testid="badan-note">
        Estimasi ini dihitung dari laporan Laba Rugi tahun {year} tanpa koreksi fiskal (positif/negatif). Untuk SPT Tahunan resmi, lakukan rekonsiliasi fiskal atas beban yang tidak dapat dikurangkan. Jenis wajib pajak & tarif dapat diubah di menu <b>Pengaturan Pajak</b>.
      </div>
    </div>
  );
}

export function TaxSettings() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let active = true;
    api("/tax-settings").then((d) => active && setSettings((s) => s ?? d)).catch((e) => active && setError(e.message));
    return () => { active = false; };
  }, []);
  if (error && !settings) return <div className="success-message error">{error}</div>;
  if (!settings) return <div className="report-loading" data-testid="report-loading">Memuat pengaturan pajak...</div>;
  const set = (key, value) => setSettings({ ...settings, [key]: value });
  const setRate23 = (key, value) => setSettings({ ...settings, pph23_rates: { ...settings.pph23_rates, [key]: value } });
  const save = async () => {
    setSaving(true); setError(""); setMessage("");
    try {
      const payload = {
        ppn_rate: Number(settings.ppn_rate), wp_type: settings.wp_type,
        pph_badan_rate: Number(settings.pph_badan_rate), pph_final_umkm_rate: Number(settings.pph_final_umkm_rate),
        pph23_non_npwp_multiplier: Number(settings.pph23_non_npwp_multiplier),
        pph23_rates: Object.fromEntries(Object.entries(settings.pph23_rates).map(([k, v]) => [k, Number(v)])),
      };
      const updated = await api("/tax-settings", { method: "PUT", body: JSON.stringify(payload) });
      setSettings(updated);
      setMessage("Pengaturan pajak berhasil disimpan.");
      setTimeout(() => setMessage(""), 5000);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  const OBJECT_LABELS = { JASA: "Jasa (teknik, manajemen, konsultan, dll)", SEWA: "Sewa harta selain tanah/bangunan", DIVIDEN: "Dividen", BUNGA: "Bunga", ROYALTI: "Royalti", HADIAH: "Hadiah & penghargaan" };
  return (
    <div data-testid="tax-settings-page">
      {message && <div className="success-message" data-testid="tax-settings-success">{message}</div>}
      {error && <div className="success-message error" data-testid="tax-settings-error">{error}</div>}
      <div className="surface tax-form">
        <h3>Jenis wajib pajak & PPh tahunan</h3>
        <div className="tax-form-grid">
          <label>Jenis wajib pajak
            <select data-testid="settings-wp-type" value={settings.wp_type} onChange={(e) => set("wp_type", e.target.value)}>
              <option value="BADAN">Badan (PT/CV) — tarif PPh Badan</option>
              <option value="UMKM_FINAL">UMKM — PPh Final dari omzet (PP 55/2022)</option>
              <option value="PRIBADI">Orang Pribadi — tarif progresif Pasal 17</option>
            </select>
          </label>
          <label>Tarif PPh Badan (%)<input data-testid="settings-badan-rate" type="number" min="0" max="100" step="0.5" value={settings.pph_badan_rate} onChange={(e) => set("pph_badan_rate", e.target.value)} /></label>
          <label>Tarif PPh Final UMKM (%)<input data-testid="settings-umkm-rate" type="number" min="0" max="100" step="0.1" value={settings.pph_final_umkm_rate} onChange={(e) => set("pph_final_umkm_rate", e.target.value)} /></label>
          <label>Tarif PPN (%)<input data-testid="settings-ppn-rate" type="number" min="0" max="100" step="0.5" value={settings.ppn_rate} onChange={(e) => set("ppn_rate", e.target.value)} /></label>
        </div>
        <p className="muted tax-hint">PPh 21 karyawan otomatis memakai tabel TER resmi (PP 58/2023) sesuai status PTKP — tidak perlu diatur manual. Orang pribadi non-final memakai tarif progresif Pasal 17 (5%–35%).</p>
      </div>
      <div className="surface tax-form">
        <h3>Tarif pemotongan PPh 23</h3>
        <div className="tax-form-grid">
          {Object.entries(settings.pph23_rates).map(([k, v]) => (
            <label key={k}>{OBJECT_LABELS[k] || k} (%)<input data-testid={`settings-pph23-${k.toLowerCase()}`} type="number" min="0" max="100" step="0.5" value={v} onChange={(e) => setRate23(k, e.target.value)} /></label>
          ))}
          <label>Pengali tanpa NPWP (×)<input data-testid="settings-non-npwp" type="number" min="1" max="5" step="0.5" value={settings.pph23_non_npwp_multiplier} onChange={(e) => set("pph23_non_npwp_multiplier", e.target.value)} /></label>
        </div>
        <div className="tax-form-actions">
          <button data-testid="tax-settings-save" className="primary-btn" disabled={saving} onClick={save}>{saving ? "Menyimpan..." : "Simpan Pengaturan"}</button>
        </div>
      </div>
    </div>
  );
}
